// ===================================================
// Open drafts — DEC-0395 «מצב טיוטה» made visible (Rule 26)
// ===================================================
// A draft is ONE bus line tagged `draft-open`. It stays open until the SAME
// sender emits `draft-merge` or `draft-archived` later. The sender lives in
// payload.from ("claude conv:<uuid>"), NOT in `subject` — bus.py writes every
// message to the recipient's room, so `subject` is the shared claude room for
// all of them.
//
// This is a TypeScript mirror of the proven SQL in
// ~/.claude/local-plugins/tiyuta/commands/drafts.md (worked 06.09.2026).
// supabase-js cannot express `group by regexp_replace(...)`, so the rows are
// fetched raw and reduced here — same grouping, same join, same cutoff.
// Read-only. Nothing here writes to the bus.

export type DraftEventRow = {
  occurred_at: string;
  tags: string[] | null;
  payload: Record<string, unknown> | null;
};

export type OpenDraft = {
  from_subject: string;
  headline: string | null;
  opened_at: string;
  age_days: number;
  state: "פתוחה" | "ישנה";
};

export const DRAFT_TAGS = ["draft-open", "draft-merge", "draft-archived"] as const;
export const STALE_AFTER_DAYS = 30;
const CLOSE_TAGS = new Set(["draft-merge", "draft-archived"]);

/** regexp_replace(payload->>'from', '^\S+\s+', '') */
function fromSubject(payload: Record<string, unknown> | null): string {
  const from = payload?.from;
  return typeof from === "string" ? from.replace(/^\S+\s+/, "") : "";
}

export function reduceOpenDrafts(rows: DraftEventRow[], now: Date = new Date()): OpenDraft[] {
  type Opened = { opened_at: string; headline: string | null };
  const opened = new Map<string, Opened>();
  const closed = new Map<string, string>();

  for (const r of rows) {
    const tags = r.tags ?? [];
    const key = fromSubject(r.payload);
    if (!key) continue;
    if (tags.includes("draft-open")) {
      const cur = opened.get(key);
      // max(occurred_at) + headline of the latest open event
      if (!cur || r.occurred_at > cur.opened_at) {
        const h = r.payload?.headline;
        opened.set(key, { opened_at: r.occurred_at, headline: typeof h === "string" ? h : null });
      }
    }
    if (tags.some((t) => CLOSE_TAGS.has(t))) {
      const cur = closed.get(key);
      if (!cur || r.occurred_at > cur) closed.set(key, r.occurred_at);
    }
  }

  const out: OpenDraft[] = [];
  for (const [from_subject, o] of opened) {
    const closedAt = closed.get(from_subject);
    // left join ... and c.closed_at > o.opened_at  where c.from_subject is null
    if (closedAt && closedAt > o.opened_at) continue;
    const ms = now.getTime() - new Date(o.opened_at).getTime();
    const age_days = Math.max(0, Math.floor(ms / 86_400_000));
    out.push({
      from_subject,
      headline: o.headline,
      opened_at: o.opened_at,
      age_days,
      // now() - opened_at > interval '30 days' — compared in ms, not in whole days
      state: ms > STALE_AFTER_DAYS * 86_400_000 ? "ישנה" : "פתוחה",
    });
  }
  // order by opened_at
  return out.sort((a, b) => a.opened_at.localeCompare(b.opened_at));
}
